/**
 * MS-05 — useFlagsRegistry (C4.3 binary)
 * --------------------------------------------------------------------------
 * Read-only hook that fetches every row from `cortex_feature_flags` and
 * groups them into operational families.
 *
 * C4.3 (2026-04): coluna `mode` e `rollout_pct` foram dropadas do schema.
 * Cada flag é estritamente binária — `enabled` true/false. Sem rollout
 * gradual, sem shadow eterno.
 */

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface FlagRow {
  flag_key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

export type FlagFamily =
  | "skill_os_v19"
  | "persona"
  | "working_memory"
  | "tom"
  | "ltm"
  | "antihal"
  | "invocation"
  | "composer"
  | "cortex_core"
  | "outros";

export const FAMILY_LABELS: Record<FlagFamily, string> = {
  skill_os_v19: "Skill OS V19",
  persona: "Persona / Personality",
  working_memory: "Working Memory",
  tom: "Theory of Mind",
  ltm: "Long-term Memory",
  antihal: "Anti-hallucination",
  invocation: "Invocation Window",
  composer: "Composer / Response policy",
  cortex_core: "Cortex Core (V19 big bang)",
  outros: "Outros",
};

export const FAMILY_ORDER: FlagFamily[] = [
  "cortex_core",
  "skill_os_v19",
  "persona",
  "working_memory",
  "tom",
  "ltm",
  "antihal",
  "invocation",
  "composer",
  "outros",
];

export function classifyFlag(key: string): FlagFamily {
  const k = key.toLowerCase();
  if (k.includes("skill_os") || k.includes("skill_matcher") || k.includes("skill_metacog")) return "skill_os_v19";
  if (k.includes("persona") || k.includes("personality")) return "persona";
  if (k.includes("wm_") || k.includes("working_memory")) return "working_memory";
  if (k.includes(".tom.") || k.startsWith("tom_") || k.includes("tom_")) return "tom";
  if (k.includes(".ltm.")) return "ltm";
  if (k.includes("antihal") || k.includes("anti_repet")) return "antihal";
  if (k.includes("invocation") || k.includes("interruption")) return "invocation";
  if (k.includes("composer") || k.includes("response_policy")) return "composer";
  if (
    k.startsWith("cortex_") ||
    k === "cortex_full_loop_enabled" ||
    k.startsWith("cortex.interoceptive") ||
    k.startsWith("cortex.pattern_sep") ||
    k.startsWith("cortex.grounding")
  ) {
    return "cortex_core";
  }
  return "outros";
}

export type FlagStatus = "on" | "off";

export function statusOf(row: FlagRow): FlagStatus {
  return row.enabled ? "on" : "off";
}

export interface FlagsRegistryState {
  rows: FlagRow[];
  byFamily: Record<FlagFamily, FlagRow[]>;
  totals: { total: number; on: number; off: number };
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useFlagsRegistry(): FlagsRegistryState {
  const [rows, setRows] = useState<FlagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("cortex_feature_flags")
          .select("flag_key, enabled, description, updated_at")
          .order("flag_key", { ascending: true });
        if (err) throw err;
        if (!cancelled) setRows((data ?? []) as FlagRow[]);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const byFamily = useMemo(() => {
    const acc: Record<FlagFamily, FlagRow[]> = {
      skill_os_v19: [],
      persona: [],
      working_memory: [],
      tom: [],
      ltm: [],
      antihal: [],
      invocation: [],
      composer: [],
      cortex_core: [],
      outros: [],
    };
    for (const r of rows) acc[classifyFlag(r.flag_key)].push(r);
    return acc;
  }, [rows]);

  const totals = useMemo(() => {
    const t = { total: rows.length, on: 0, off: 0 };
    for (const r of rows) {
      if (r.enabled) t.on += 1;
      else t.off += 1;
    }
    return t;
  }, [rows]);

  return {
    rows,
    byFamily,
    totals,
    loading,
    error,
    refetch: () => setTick((t) => t + 1),
  };
}
